import React from 'react';

/**
 * Loading indicator component with size variants
 * 
 * @param {object} props - Component props
 * @param {string} props.size - Size of the spinner: 'small', 'medium', 'large'
 * @param {string} props.color - Color of the spinner: 'primary', 'gray', 'white'
 * @param {string} props.text - Optional text to display below the spinner
 * @param {string} props.className - Additional CSS classes
 */
const LoadingIndicator = ({ 
  size = 'medium', 
  color = 'primary',
  text,
  className = ''
}) => {
  // Size classes
  const sizeClasses = {
    small: 'h-4 w-4 border-2',
    medium: 'h-8 w-8 border-2',
    large: 'h-12 w-12 border-3',
  };

  // Color classes
  const colorClasses = {
    primary: 'border-indigo-500',
    gray: 'border-gray-500',
    white: 'border-white',
  };

  const textSizeClasses = {
    small: 'text-xs',
    medium: 'text-sm',
    large: 'text-base',
  };

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <div
        className={`rounded-full ${sizeClasses[size] || sizeClasses.medium} border-b-transparent ${colorClasses[color] || colorClasses.primary} animate-spin`}
      ></div>
      {text && (
        <p className={`mt-2 ${textSizeClasses[size] || textSizeClasses.medium} text-gray-500`}>
          {text}
        </p>
      )}
    </div>
  );
};

/**
 * Full screen loading overlay
 */
export const FullScreenLoading = ({ message = 'Loading...' }) => {
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
        <LoadingIndicator size="large" color="primary" />
        <p className="mt-4 text-gray-700 font-medium">{message}</p>
      </div>
    </div>
  );
};

/**
 * Button with loading state
 */
export const LoadingButton = ({ 
  isLoading, 
  loadingText = 'Loading...', 
  children, 
  disabled, 
  className = '', 
  ...props 
}) => {
  return (
    <button
      disabled={isLoading || disabled}
      className={`relative inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
        isLoading || disabled ? 'opacity-70 cursor-not-allowed' : ''
      } ${className}`}
      {...props}
    >
      {isLoading ? (
        <>
          <span className="absolute left-0 inset-y-0 flex items-center pl-3">
            <LoadingIndicator size="small" color={className.includes('bg-white') ? 'primary' : 'white'} />
          </span>
          <span className="pl-6">{loadingText}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
};

export default LoadingIndicator;